import jsPDF from "jspdf";
import { fmt } from "@/components/pipe/pipe-helpers";
import {
  getWallFactor,
  materialOptions,
  weldTypeOptions,
  npsLabel,
  type WeldTypeId,
} from "./fdi-data";

interface WeldEntry {
  id: string;
  qty: number;
  nps: number;
  wall: string;
  materialIdx: number;
  weldTypeId: WeldTypeId;
  branchNps: number;
  headerWall: string;
  linearInches: string;
}

const MARGIN = 15;
const PAGE_W = 210;
const CONTENT_W = PAGE_W - MARGIN * 2;

const calcFDI = (e: WeldEntry): number => {
  const mat = materialOptions[e.materialIdx];
  const wt = weldTypeOptions.find(w => w.id === e.weldTypeId)!;
  const wallNum = parseFloat(e.wall) || 0;
  const wallFactor = getWallFactor(wallNum);
  if (e.weldTypeId === "linear") {
    const lin = parseFloat(e.linearInches) || 0;
    return lin * Math.PI * mat.factor * wallFactor * e.qty;
  }
  if (!wt.usesBranch) {
    return e.nps * wallFactor * mat.factor * wt.factor * e.qty;
  }
  let result = e.branchNps * wt.branchMultiplier * mat.factor;
  if (wt.usesHeaderWF) {
    result *= getWallFactor(parseFloat(e.headerWall) || 0);
  } else if (wt.usesNozzleWF) {
    result *= wallFactor;
  }
  return result * e.qty;
};

export const generateFdiPDF = (entries: WeldEntry[], jobName?: string) => {
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  const pageH = doc.internal.pageSize.getHeight();
  let y = MARGIN;

  const checkPage = (needed: number) => {
    if (y + needed > pageH - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  // Header
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("PFI TB-10 — Factored Weld Inches", MARGIN, y + 5);
  y += 10;

  if (jobName) {
    doc.setFontSize(10);
    doc.text(jobName, MARGIN, y);
    y += 5;
  }

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120);
  doc.text(`Generated ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, MARGIN, y);
  y += 4;
  doc.text("FDI = NPS × Wall Factor × Material Factor × Weld Type Factor", MARGIN, y);
  doc.setTextColor(0);
  y += 6;

  // Divider
  doc.setDrawColor(180);
  doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
  y += 5;

  // Table header
  const cols = [MARGIN, MARGIN + 8, MARGIN + 18, MARGIN + 42, MARGIN + 62, MARGIN + 92, MARGIN + 122, MARGIN + 148];
  const colLabels = ["#", "Qty", "Size / Linear", "Wall", "Material", "Weld Type", "Factors", "FDI"];

  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setFillColor(240, 240, 240);
  doc.rect(MARGIN, y - 3, CONTENT_W, 5, "F");
  colLabels.forEach((label, i) => doc.text(label, cols[i], y));
  y += 6;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);

  const totalFDI = entries.reduce((sum, e) => sum + calcFDI(e), 0);

  entries.forEach((e, idx) => {
    checkPage(12);
    const wt = weldTypeOptions.find(w => w.id === e.weldTypeId)!;
    const mat = materialOptions[e.materialIdx];
    const fdi = calcFDI(e);
    const wf = getWallFactor(parseFloat(e.wall) || 0);

    const sizeStr = e.weldTypeId === "linear"
      ? `${e.linearInches}" linear`
      : npsLabel(e.nps) + (wt.usesBranch ? ` (br: ${npsLabel(e.branchNps)})` : "");

    // Alternate row shading
    if (idx % 2 === 0) {
      doc.setFillColor(248, 248, 248);
      doc.rect(MARGIN, y - 3.5, CONTENT_W, 5, "F");
    }

    doc.text(String(idx + 1), cols[0], y);
    doc.text(String(e.qty), cols[1], y);
    doc.text(sizeStr, cols[2], y);
    doc.text(e.wall + '"', cols[3], y);
    doc.text(mat.pNumber, cols[4], y);
    doc.text(wt.label, cols[5], y);
    doc.setFontSize(7);
    doc.text(`WF:${wf} MF:${mat.factor} WT:${wt.usesBranch ? wt.branchMultiplier : wt.factor}`, cols[6], y);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(fmt(fdi, 1), cols[7], y);
    doc.setFont("helvetica", "normal");

    y += 5;
  });

  // Total
  y += 2;
  doc.setDrawColor(180);
  doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
  y += 6;

  checkPage(10);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Total FDI:", MARGIN, y);
  doc.text(`${fmt(totalFDI, 1)}`, MARGIN + CONTENT_W, y, { align: "right" });
  y += 6;

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120);
  doc.text(`${entries.length} weld(s) calculated per PFI TB-10 standard`, MARGIN, y);

  doc.save(`FDI_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
};
